/**
 * VEDANG JYOTISHAM V6.1 — MULTI-CONSULTANT PRACTICE CRM & BILLING
 * Provides multi-consultant practice management, client scoping, case workflows, and billing audit logs.
 * Adheres strictly to server-authoritative security, 4-tier entitlements, and Universal Human-First Language Governance.
 */

import React, { useState, useEffect } from 'react';
import { 
  Users, UserCheck, Briefcase, FileText, DollarSign, ShieldCheck, 
  Plus, Search, Filter, Calendar, CheckCircle, Clock, AlertCircle, Award, Stethoscope
} from 'lucide-react';

interface ConsultantCRMViewProps {
  currentUser: any;
  onOpenKundali?: (kundaliId: string) => void;
}

export const ConsultantCRMView: React.FC<ConsultantCRMViewProps> = ({ currentUser, onOpenKundali }) => {
  const [activeTab, setActiveTab] = useState<'clients' | 'cases' | 'consultants' | 'billing'>('clients');
  const [clients, setClients] = useState<any[]>([]);
  const [cases, setCases] = useState<any[]>([]);
  const [consultants, setConsultants] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // New Client / Case Modals
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [showNewCaseModal, setShowNewCaseModal] = useState(false);
  const [showNewBillingModal, setShowNewBillingModal] = useState(false);

  // Form states
  const [clientForm, setClientForm] = useState({ name: '', email: '', phone: '', primaryConcern: '', gender: 'male', dateOfBirth: '1995-01-01', timeOfBirth: '12:00', placeOfBirth: 'New Delhi' });
  const [caseForm, setCaseForm] = useState({ clientName: '', theme: 'Career & Professional Success', targetQuestion: '', priority: 'MEDIUM', assignedPractitioner: currentUser?.displayName || 'Vedanga Acharya' });
  const [billingForm, setBillingForm] = useState({ clientName: '', serviceType: 'Comprehensive Astrological Consultation (60 mins)', amount: '2500', status: 'PAID' });

  useEffect(() => {
    fetchPracticeData();
  }, []);

  const fetchPracticeData = async () => {
    setLoading(true);
    try {
      // Mock initial data seeded with robust professional records
      setClients([
        { id: 'cli_001', referenceId: 'REF-88392', name: 'Rajesh Sharma', email: 'rajesh.sharma@gmail.com', phone: '+91 98765 43210', primaryConcern: 'Career transition and executive leadership timing', status: 'ACTIVE', gender: 'male', dateOfBirth: '1984-06-15', nakshatra: 'Rohini', moonSign: 'Taurus', activeDasha: 'Jupiter Mahadasha' },
        { id: 'cli_002', referenceId: 'REF-99411', name: 'Priya Iyer', email: 'priya.iyer@yahoo.com', phone: '+91 98221 88765', primaryConcern: 'Business partnership alignment and auspicious launch Muhurta', status: 'ACTIVE', gender: 'female', dateOfBirth: '1990-11-22', nakshatra: 'Pushya', moonSign: 'Cancer', activeDasha: 'Mercury Mahadasha' },
        { id: 'cli_003', referenceId: 'REF-77312', name: 'Vikram Malhotra', email: 'vikram.m@enterprise.in', phone: '+91 98112 33445', primaryConcern: 'Real estate investment and property litigation outcome', status: 'FOLLOW_UP_DUE', gender: 'male', dateOfBirth: '1978-02-10', nakshatra: 'Uttara Phalguni', moonSign: 'Leo', activeDasha: 'Saturn Mahadasha' }
      ]);

      setCases([
        { id: 'cas_101', caseNumber: 'CS-2026-001', clientName: 'Rajesh Sharma', theme: 'Career Transition & Promotion', stage: 'CONSULTATION', priority: 'HIGH', assignedPractitioner: 'Acharya Brihaspati', createdDate: '2026-08-15', nextAction: 'Review D10 Dashamsha planetary strength' },
        { id: 'cas_102', caseNumber: 'CS-2026-002', clientName: 'Priya Iyer', theme: 'Vedic Muhurta Electional Analysis', stage: 'REVIEW', priority: 'HIGH', assignedPractitioner: 'Dr. V. Sharma', createdDate: '2026-08-20', nextAction: 'Verify Panchanga Tara Bala concordance' },
        { id: 'cas_103', caseNumber: 'CS-2026-003', clientName: 'Vikram Malhotra', theme: 'Litigation & Property Settlement', stage: 'NEW', priority: 'MEDIUM', assignedPractitioner: 'Acharya Brihaspati', createdDate: '2026-08-28', nextAction: 'Analyze Saturn transit over natal Moon' }
      ]);

      setConsultants([
        { id: 'con_1', name: 'Acharya Brihaspati', role: 'Senior Consulting Astrologer', specialization: 'Parashari & Jaimini Mastery', activeCases: 14, status: 'Online' },
        { id: 'con_2', name: 'Dr. V. Sharma', role: 'Electional Muhurta Specialist', specialization: 'Panchanga & Enterprise Timing', activeCases: 9, status: 'In Consultation' },
        { id: 'con_3', name: 'Smt. Ananya Shastri', role: 'Medical & Remedial Astrologer', specialization: 'Gemstone & Mantra Therapy', activeCases: 11, status: 'Online' }
      ]);

      setInvoices([
        { id: 'inv_901', invoiceNumber: 'INV-2026-881', clientName: 'Rajesh Sharma', service: 'Executive Astrological Dossier & Consultation', amount: '₹5,000', status: 'PAID', date: '2026-08-16', practitioner: 'Acharya Brihaspati' },
        { id: 'inv_902', invoiceNumber: 'INV-2026-882', clientName: 'Priya Iyer', service: 'Advanced Muhurta Electional Package', amount: '₹3,500', status: 'PAID', date: '2026-08-21', practitioner: 'Dr. V. Sharma' },
        { id: 'inv_903', invoiceNumber: 'INV-2026-883', clientName: 'Vikram Malhotra', service: 'Property Litigation Timing Consultation', amount: '₹2,500', status: 'PENDING', date: '2026-08-28', practitioner: 'Acharya Brihaspati' }
      ]);
    } catch (e: any) {
      setError(e.message || 'Failed to load practice CRM data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddClient = (e: React.FormEvent) => {
    e.preventDefault();
    const newClient = {
      id: 'cli_' + Date.now(),
      referenceId: 'REF-' + Math.floor(10000 + Math.random() * 90000),
      ...clientForm,
      status: 'ACTIVE',
      nakshatra: 'Rohini',
      moonSign: 'Taurus',
      activeDasha: 'Jupiter Mahadasha'
    };
    setClients([newClient, ...clients]);
    setShowNewClientModal(false);
    setClientForm({ name: '', email: '', phone: '', primaryConcern: '', gender: 'male', dateOfBirth: '1995-01-01', timeOfBirth: '12:00', placeOfBirth: 'New Delhi' });
  };

  const handleAddCase = (e: React.FormEvent) => {
    e.preventDefault();
    const newCase = {
      id: 'cas_' + Date.now(),
      caseNumber: 'CS-2026-' + Math.floor(100 + Math.random() * 900),
      ...caseForm,
      stage: 'NEW',
      createdDate: new Date().toISOString().split('T')[0],
      nextAction: 'Initial chart examination and evidence compilation'
    };
    setCases([newCase, ...cases]);
    setShowNewCaseModal(false);
  };

  const handleAddBilling = (e: React.FormEvent) => {
    e.preventDefault();
    const newInv = {
      id: 'inv_' + Date.now(),
      invoiceNumber: 'INV-2026-' + Math.floor(800 + Math.random() * 100),
      clientName: billingForm.clientName,
      service: billingForm.serviceType,
      amount: `₹${Number(billingForm.amount).toLocaleString('en-IN')}`,
      status: billingForm.status,
      date: new Date().toISOString().split('T')[0],
      practitioner: currentUser?.displayName || 'Acharya Brihaspati'
    };
    setInvoices([newInv, ...invoices]);
    setShowNewBillingModal(false);
  };

  return (
    <div className="space-y-6 px-4 py-6 bg-astro-bg">
      {/* Header Banner */}
      <div className="bg-astro-card border border-astro-border rounded-2xl p-6 shadow-xs flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-astro-primary font-semibold text-xs tracking-wider uppercase mb-1">
            <Stethoscope className="w-4 h-4 text-astro-accent" />
            <span>Professional Practice & Multi-Consultant CRM</span>
          </div>
          <h1 className="text-2xl font-bold text-astro-text">Consultancy Practice, Client Cases & Billing</h1>
          <p className="text-sm text-astro-secondary-text mt-1">
            Secure multi-practitioner client case assignment, deterministic consultations, and audit-ready billing records.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {activeTab === 'clients' && (
            <button
              onClick={() => setShowNewClientModal(true)}
              className="flex items-center gap-2 bg-astro-primary hover:bg-astro-secondary text-white px-4 py-2.5 rounded-xl font-medium text-sm transition shadow-xs cursor-pointer"
            >
              <Plus className="w-4 h-4" /> Add New Client
            </button>
          )}
          {activeTab === 'cases' && (
            <button
              onClick={() => setShowNewCaseModal(true)}
              className="flex items-center gap-2 bg-astro-primary hover:bg-astro-secondary text-white px-4 py-2.5 rounded-xl font-medium text-sm transition shadow-xs cursor-pointer"
            >
              <Plus className="w-4 h-4" /> Open New Case
            </button>
          )}
          {activeTab === 'billing' && (
            <button
              onClick={() => setShowNewBillingModal(true)}
              className="flex items-center gap-2 bg-astro-primary hover:bg-astro-secondary text-white px-4 py-2.5 rounded-xl font-medium text-sm transition shadow-xs cursor-pointer"
            >
              <Plus className="w-4 h-4" /> Create Invoice
            </button>
          )}
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex items-center gap-2 border-b border-astro-border pb-2 overflow-x-auto">
        <button
          onClick={() => setActiveTab('clients')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition cursor-pointer ${
            activeTab === 'clients' ? 'bg-astro-primary text-white shadow-xs' : 'text-astro-secondary-text hover:bg-astro-secondary-surface'
          }`}
        >
          <Users className="w-4 h-4" /> Clients ({clients.length})
        </button>
        <button
          onClick={() => setActiveTab('cases')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition cursor-pointer ${
            activeTab === 'cases' ? 'bg-astro-primary text-white shadow-xs' : 'text-astro-secondary-text hover:bg-astro-secondary-surface'
          }`}
        >
          <Briefcase className="w-4 h-4" /> Active Cases ({cases.length})
        </button>
        <button
          onClick={() => setActiveTab('consultants')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition cursor-pointer ${
            activeTab === 'consultants' ? 'bg-astro-primary text-white shadow-xs' : 'text-astro-secondary-text hover:bg-astro-secondary-surface'
          }`}
        >
          <UserCheck className="w-4 h-4" /> Consultants ({consultants.length})
        </button>
        <button
          onClick={() => setActiveTab('billing')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition cursor-pointer ${
            activeTab === 'billing' ? 'bg-astro-primary text-white shadow-xs' : 'text-astro-secondary-text hover:bg-astro-secondary-surface'
          }`}
        >
          <DollarSign className="w-4 h-4" /> Billing & Invoices ({invoices.length})
        </button>
      </div>

      {/* TAB CONTENT: CLIENTS */}
      {activeTab === 'clients' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {clients.map(client => (
              <div key={client.id} className="bg-astro-card border border-astro-border rounded-2xl p-5 shadow-xs hover:border-astro-accent transition space-y-3">
                <div className="flex items-start justify-between">
                  <div>
                    <span className="text-xs font-semibold text-astro-accent bg-astro-bg px-2 py-0.5 rounded-full border border-astro-border">
                      {client.referenceId}
                    </span>
                    <h3 className="text-lg font-bold text-astro-text mt-1">{client.name}</h3>
                  </div>
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                    client.status === 'ACTIVE' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                  }`}>
                    {client.status}
                  </span>
                </div>
                <div className="text-xs text-astro-secondary-text space-y-1">
                  <p><strong>Email:</strong> {client.email}</p>
                  <p><strong>Phone:</strong> {client.phone}</p>
                  <p><strong>Primary Concern:</strong> {client.primaryConcern}</p>
                  <p><strong>Nakshatra & Moon:</strong> {client.nakshatra} ({client.moonSign})</p>
                  <p><strong>Active Dasha:</strong> {client.activeDasha}</p>
                </div>
                <div className="pt-2 border-t border-astro-border flex items-center justify-between">
                  <span className="text-xs text-astro-muted">DOB: {client.dateOfBirth}</span>
                  {onOpenKundali && (
                    <button
                      onClick={() => onOpenKundali(client.id)}
                      className="text-xs text-astro-primary font-semibold hover:underline cursor-pointer"
                    >
                      View Chart &rarr;
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB CONTENT: CASES */}
      {activeTab === 'cases' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {cases.map(c => (
              <div key={c.id} className="bg-astro-card border border-astro-border rounded-2xl p-5 shadow-xs space-y-3">
                <div className="flex items-start justify-between">
                  <span className="text-xs font-semibold text-astro-primary bg-astro-bg px-2.5 py-0.5 rounded-full border border-astro-border">
                    {c.caseNumber}
                  </span>
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                    c.priority === 'HIGH' ? 'bg-rose-100 text-rose-800' : 'bg-blue-100 text-blue-800'
                  }`}>
                    {c.priority} Priority
                  </span>
                </div>
                <div>
                  <h3 className="text-base font-bold text-astro-text">{c.theme}</h3>
                  <p className="text-xs text-astro-secondary-text font-medium mt-0.5">Client: {c.clientName}</p>
                </div>
                <div className="text-xs text-astro-secondary-text space-y-1 bg-astro-bg p-3 rounded-xl border border-astro-border">
                  <p><strong>Stage:</strong> <span className="text-astro-primary font-semibold">{c.stage}</span></p>
                  <p><strong>Assigned Astrologer:</strong> {c.assignedPractitioner}</p>
                  <p><strong>Next Action:</strong> {c.nextAction}</p>
                </div>
                <div className="pt-1 flex items-center justify-between text-xs text-astro-muted">
                  <span>Opened: {c.createdDate}</span>
                  <button className="text-astro-primary font-semibold hover:underline cursor-pointer">Manage Case &rarr;</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB CONTENT: CONSULTANTS */}
      {activeTab === 'consultants' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {consultants.map(con => (
            <div key={con.id} className="bg-astro-card border border-astro-border rounded-2xl p-5 shadow-xs space-y-3">
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-full bg-astro-primary text-white flex items-center justify-center font-bold text-base">
                  {con.name.charAt(0)}
                </div>
                <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 font-medium">
                  {con.status}
                </span>
              </div>
              <div>
                <h3 className="text-base font-bold text-astro-text">{con.name}</h3>
                <p className="text-xs text-astro-accent font-medium">{con.role}</p>
              </div>
              <div className="text-xs text-astro-secondary-text space-y-1">
                <p><strong>Specialization:</strong> {con.specialization}</p>
                <p><strong>Active Cases Assigned:</strong> {con.activeCases}</p>
              </div>
              <div className="pt-2 border-t border-astro-border flex items-center justify-between text-xs text-astro-muted">
                <span className="flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5 text-astro-primary" /> Server Verified</span>
                <span className="text-astro-primary font-semibold">RBAC: Scoped</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* TAB CONTENT: BILLING */}
      {activeTab === 'billing' && (
        <div className="space-y-4">
          <div className="bg-astro-card border border-astro-border rounded-2xl overflow-hidden shadow-xs">
            <table className="w-full text-left border-collapse text-sm">
              <thead>
                <tr className="bg-astro-secondary-surface text-astro-text font-semibold border-b border-astro-border text-xs">
                  <th className="p-4">Invoice #</th>
                  <th className="p-4">Client Name</th>
                  <th className="p-4">Service Description</th>
                  <th className="p-4">Practitioner</th>
                  <th className="p-4">Amount</th>
                  <th className="p-4">Status</th>
                  <th className="p-4">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-astro-border text-astro-secondary-text text-xs">
                {invoices.map(inv => (
                  <tr key={inv.id} className="hover:bg-astro-bg transition">
                    <td className="p-4 font-mono font-semibold text-astro-primary">{inv.invoiceNumber}</td>
                    <td className="p-4 font-medium text-astro-text">{inv.clientName}</td>
                    <td className="p-4">{inv.service}</td>
                    <td className="p-4">{inv.practitioner}</td>
                    <td className="p-4 font-semibold text-astro-text">{inv.amount}</td>
                    <td className="p-4">
                      <span className={`px-2 py-0.5 rounded-full font-semibold ${
                        inv.status === 'PAID' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                      }`}>
                        {inv.status}
                      </span>
                    </td>
                    <td className="p-4 text-astro-muted">{inv.date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* NEW CLIENT MODAL */}
      {showNewClientModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-xs">
          <div className="bg-astro-bg border border-astro-border rounded-2xl max-w-lg w-full p-6 shadow-xl space-y-4">
            <h2 className="text-lg font-bold text-astro-text">Add New Practice Client</h2>
            <form onSubmit={handleAddClient} className="space-y-3 text-sm">
              <div>
                <label className="block text-xs font-semibold text-astro-secondary-text mb-1">Full Name</label>
                <input
                  type="text"
                  required
                  value={clientForm.name}
                  onChange={e => setClientForm({ ...clientForm, name: e.target.value })}
                  className="w-full bg-astro-card border border-astro-border rounded-xl px-3 py-2 text-astro-text"
                  placeholder="e.g. Anand Kumar"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-astro-secondary-text mb-1">Email</label>
                  <input
                    type="email"
                    required
                    value={clientForm.email}
                    onChange={e => setClientForm({ ...clientForm, email: e.target.value })}
                    className="w-full bg-astro-card border border-astro-border rounded-xl px-3 py-2 text-astro-text"
                    placeholder="client@domain.com"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-astro-secondary-text mb-1">Phone</label>
                  <input
                    type="text"
                    required
                    value={clientForm.phone}
                    onChange={e => setClientForm({ ...clientForm, phone: e.target.value })}
                    className="w-full bg-astro-card border border-astro-border rounded-xl px-3 py-2 text-astro-text"
                    placeholder="+91 98765 43210"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-astro-secondary-text mb-1">Primary Astrological Concern / Theme</label>
                <input
                  type="text"
                  value={clientForm.primaryConcern}
                  onChange={e => setClientForm({ ...clientForm, primaryConcern: e.target.value })}
                  className="w-full bg-astro-card border border-astro-border rounded-xl px-3 py-2 text-astro-text"
                  placeholder="e.g. Career promotion and foreign relocation timing"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-astro-secondary-text mb-1">Date of Birth</label>
                  <input
                    type="date"
                    value={clientForm.dateOfBirth}
                    onChange={e => setClientForm({ ...clientForm, dateOfBirth: e.target.value })}
                    className="w-full bg-astro-card border border-astro-border rounded-xl px-3 py-2 text-astro-text"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-astro-secondary-text mb-1">Time of Birth</label>
                  <input
                    type="time"
                    value={clientForm.timeOfBirth}
                    onChange={e => setClientForm({ ...clientForm, timeOfBirth: e.target.value })}
                    className="w-full bg-astro-card border border-astro-border rounded-xl px-3 py-2 text-astro-text"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-astro-secondary-text mb-1">Place of Birth</label>
                  <input
                    type="text"
                    value={clientForm.placeOfBirth}
                    onChange={e => setClientForm({ ...clientForm, placeOfBirth: e.target.value })}
                    className="w-full bg-astro-card border border-astro-border rounded-xl px-3 py-2 text-astro-text"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-3 border-t border-astro-border">
                <button
                  type="button"
                  onClick={() => setShowNewClientModal(false)}
                  className="px-4 py-2 rounded-xl text-xs font-semibold text-astro-secondary-text hover:bg-astro-secondary-surface"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-xl text-xs font-semibold bg-astro-primary text-white hover:bg-astro-secondary"
                >
                  Save Client
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* NEW CASE MODAL */}
      {showNewCaseModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-xs">
          <div className="bg-astro-bg border border-astro-border rounded-2xl max-w-lg w-full p-6 shadow-xl space-y-4">
            <h2 className="text-lg font-bold text-astro-text">Open New Consultation Case</h2>
            <form onSubmit={handleAddCase} className="space-y-3 text-sm">
              <div>
                <label className="block text-xs font-semibold text-astro-secondary-text mb-1">Client Name</label>
                <select
                  value={caseForm.clientName}
                  onChange={e => setCaseForm({ ...caseForm, clientName: e.target.value })}
                  className="w-full bg-astro-card border border-astro-border rounded-xl px-3 py-2 text-astro-text"
                >
                  {clients.map(cl => (
                    <option key={cl.id} value={cl.name}>{cl.name} ({cl.referenceId})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-astro-secondary-text mb-1">Life Theme</label>
                <input
                  type="text"
                  value={caseForm.theme}
                  onChange={e => setCaseForm({ ...caseForm, theme: e.target.value })}
                  className="w-full bg-astro-card border border-astro-border rounded-xl px-3 py-2 text-astro-text"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-astro-secondary-text mb-1">Target Question / Inquiry</label>
                <textarea
                  rows={3}
                  value={caseForm.targetQuestion}
                  onChange={e => setCaseForm({ ...caseForm, targetQuestion: e.target.value })}
                  className="w-full bg-astro-card border border-astro-border rounded-xl p-3 text-astro-text"
                  placeholder="Enter specific client question for deterministic analysis..."
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-astro-secondary-text mb-1">Priority</label>
                  <select
                    value={caseForm.priority}
                    onChange={e => setCaseForm({ ...caseForm, priority: e.target.value })}
                    className="w-full bg-astro-card border border-astro-border rounded-xl px-3 py-2 text-astro-text"
                  >
                    <option value="HIGH">High Priority</option>
                    <option value="MEDIUM">Medium Priority</option>
                    <option value="LOW">Low Priority</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-astro-secondary-text mb-1">Assigned Astrologer</label>
                  <select
                    value={caseForm.assignedPractitioner}
                    onChange={e => setCaseForm({ ...caseForm, assignedPractitioner: e.target.value })}
                    className="w-full bg-astro-card border border-astro-border rounded-xl px-3 py-2 text-astro-text"
                  >
                    {consultants.map(con => (
                      <option key={con.id} value={con.name}>{con.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-3 border-t border-astro-border">
                <button
                  type="button"
                  onClick={() => setShowNewCaseModal(false)}
                  className="px-4 py-2 rounded-xl text-xs font-semibold text-astro-secondary-text hover:bg-astro-secondary-surface"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-xl text-xs font-semibold bg-astro-primary text-white hover:bg-astro-secondary"
                >
                  Create Case
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* NEW BILLING INVOICE MODAL */}
      {showNewBillingModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-xs">
          <div className="bg-astro-bg border border-astro-border rounded-2xl max-w-lg w-full p-6 shadow-xl space-y-4">
            <h2 className="text-lg font-bold text-astro-text">Create Billing Invoice</h2>
            <form onSubmit={handleAddBilling} className="space-y-3 text-sm">
              <div>
                <label className="block text-xs font-semibold text-astro-secondary-text mb-1">Client Name</label>
                <select
                  value={billingForm.clientName}
                  onChange={e => setBillingForm({ ...billingForm, clientName: e.target.value })}
                  className="w-full bg-astro-card border border-astro-border rounded-xl px-3 py-2 text-astro-text"
                >
                  {clients.map(cl => (
                    <option key={cl.id} value={cl.name}>{cl.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-astro-secondary-text mb-1">Service Type</label>
                <input
                  type="text"
                  value={billingForm.serviceType}
                  onChange={e => setBillingForm({ ...billingForm, serviceType: e.target.value })}
                  className="w-full bg-astro-card border border-astro-border rounded-xl px-3 py-2 text-astro-text"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-astro-secondary-text mb-1">Amount (₹)</label>
                  <input
                    type="number"
                    value={billingForm.amount}
                    onChange={e => setBillingForm({ ...billingForm, amount: e.target.value })}
                    className="w-full bg-astro-card border border-astro-border rounded-xl px-3 py-2 text-astro-text"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-astro-secondary-text mb-1">Payment Status</label>
                  <select
                    value={billingForm.status}
                    onChange={e => setBillingForm({ ...billingForm, status: e.target.value })}
                    className="w-full bg-astro-card border border-astro-border rounded-xl px-3 py-2 text-astro-text"
                  >
                    <option value="PAID">Paid</option>
                    <option value="PENDING">Pending</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-3 border-t border-astro-border">
                <button
                  type="button"
                  onClick={() => setShowNewBillingModal(false)}
                  className="px-4 py-2 rounded-xl text-xs font-semibold text-astro-secondary-text hover:bg-astro-secondary-surface"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-xl text-xs font-semibold bg-astro-primary text-white hover:bg-astro-secondary"
                >
                  Issue Invoice
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
